import type { Editor } from '@tiptap/core';
import EditorCache from '../editorcache';
// Not the wikitext barrel: its schemas import @tiptap/core and every
// extension, which would put the whole editor on pages that only read text.
import serializer from '../wikitext/to_wiki';

interface LegacyEditorHandle {
   legacy: true;
   value: string;
   text: string;
   el: any;
   editable: any;
}

const isLegacy = (editor: any): boolean => Boolean(editor?.legacy);

export const setEditor = (editorid: string, editor: Editor) => {
   EditorCache.set(editorid, editor);
};

export const removeEditor = (editorid: string) => {
   EditorCache.delete(editorid);
};

export const getEditor = (editorid: string): Editor | null => {
   const editor = EditorCache.get(editorid);
   if (isLegacy(editor)) {
      return null;
   }
   return editor;
};

const getEditorOrLegacy = (editorid: string): Editor | LegacyEditorHandle =>
   EditorCache.get(editorid);

export const getSerializedText = (editorid: string): string => {
   const editor = getEditorOrLegacy(editorid);
   if (isLegacy(editor)) {
      return (editor as LegacyEditorHandle).text;
   }
   const doc = (editor as Editor)?.state?.doc;
   return doc ? serializer.serialize(doc) : '';
};

/**
 * Whether there is anything to read text off yet. The cache stays empty until
 * the editor commits, which is after the page is interactive, so a handler that
 * reads the text has to decide what an early click means. Without this it
 * cannot tell an unloaded editor from an empty one.
 */
export const isEditorReady = (editorid: string): boolean => Boolean(getEditorOrLegacy(editorid));

export const getText = (editorid: string): string => {
   const editor = getEditorOrLegacy(editorid);
   if (isLegacy(editor)) {
      return (editor as LegacyEditorHandle).text;
   }
   // The `as Editor` cast hides the empty cache from the return type. Callers
   // treat this as a string, so an unloaded editor reads as no text; check
   // isEditorReady first when the difference matters.
   return (editor as Editor)?.getText() ?? '';
};

export const focusEditor = (editorid: string) => {
   const editor = getEditor(editorid);
   editor?.commands?.focus();
};

export const clearContent = (editorid: string) => {
   const editor = getEditor(editorid);
   editor?.commands?.clearContent(false);
   editor?.commands?.updateSavedContent();
};

export const resetContent = (editorid: string) => {
   const editor = getEditor(editorid);
   // Destroyed editors reset storage to {}, so savedContent can be missing.
   const savedContent = editor?.storage?.originalState?.savedContent;
   if (savedContent) {
      editor.commands?.setContent(savedContent.toJSON(), { emitUpdate: false });
   }
};

export const saveState = (editorid: string) => {
   const editor = getEditor(editorid);
   editor?.commands?.updateSavedContent();
};

export const clearSessionStorage = (editorid: string) => {
   const editor = getEditor(editorid);
   editor?.commands?.clearSessionStorage();
};

export const getCharacterCount = (editorid: string) => {
   const editor = getEditor(editorid);
   return editor?.storage?.characterCount?.characters();
};

export const clearError = (editorid: string) => {
   const editor = getEditor(editorid);
   editor?.commands?.clearError();
};

export const hasUnsavedChanges = (editorid: string) => {
   const editor = getEditor(editorid);
   return editor?.storage?.originalState?.hasUnsavedChanges;
};
